import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, Input, ViewChild, ElementRef,
  ChangeDetectorRef, HostListener } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { EMPTY, Subject, Subscription, interval } from 'rxjs';
import { takeUntil, take, filter, catchError } from 'rxjs/operators';
import { DocumentImageAnnotatorComponent } from '../../document/document-image-annotator/document-image-annotator.component';
import { MediaUploadDialogComponent } from '../media-upload-dialog/media-upload-dialog.component';
import { DocumentCreateFolderComponent } from '../../document/document-create-folder/document-create-folder.component';
import { ConfirmationPopupComponent } from '../confirmation-popup/confirmation-popup.component';
import { DocumentService } from '../../document/document.service';
import { AttachmentsServiceService } from '../../../services/attachments/attachments-service.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { HelperService } from 'src/app/services/helper.service';
import { FirebaseEventService } from '../../../services/core/notification/firebase-event.service';

interface MediaItem {
  id: number;
  name: string;
  url: string;
  thumbnail_url: string;
  file_type: string;
  type?: string;                // 'folder' or 'file'
  size: number;
  date: Date;
  folder_id?: number;
  tags?: string[];
  description?: string;
  selected?: boolean;
  editing?: boolean;
  is_primary?: boolean;
  uploaded_by?: string;        // User name who uploaded
  user_avatar?: string;         // Avatar URL
  user_initials?: string;       // Fallback initials
  document_id?: number;         // Parent document ID for folders
  annotations?: any[];          // Saved image markup (loaded into the annotator on open)
  comments?: any[];             // Saved image comments (loaded into the annotator on open)
  delivery_code?: string | null; // Linked delivery code (warehouse mirror)
  vendor_name?: string | null;   // Source delivery's vendor (warehouse mirror)
  po_number?: string | null;     // Source delivery's PO number (warehouse mirror)
  job_name?: string | null;      // Source delivery's job name(s) (warehouse mirror)
}

interface Folder {
  id: number;
  name: string;
  count: number;
}

@Component({
    selector: 'app-jobsite-gallery',
    templateUrl: './jobsite-gallery.component.html',
    styleUrls: ['./jobsite-gallery.component.scss'],
    standalone: false,
    animations: [
        // Copied from Task Attachments so the drag overlay and upload panel
        // enter from the top exactly as they do there.
        trigger('slideInFromTop', [
            transition(':enter', [
                style({ transform: 'translateY(-100%)', opacity: 0 }),
                animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 })),
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateY(-100%)', opacity: 0 })),
            ]),
        ]),
    ]
})
export class JobsiteGalleryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() moduleId: number = 19; // Job module ID
  @Input() recordId?: number;
  @Input() viewType: string = 'detail';
  /** Heading shown in the gallery header. Overridable per host (e.g.
   *  "Warehouse Gallery"); defaults to the legacy "Jobsite Gallery". */
  @Input() galleryTitle: string = 'Jobsite Gallery';
  /** Warehouse-only: show a "Filter by delivery" dropdown driven by each
   *  file's source `delivery_code`. Default off, so other modules are
   *  unaffected. */
  @Input() enableDeliveryFilter: boolean = false;
  /** Warehouse-only: when on, the image preview lightbox gets an info icon
   *  that opens a small details panel (uploaded by, module, delivery, etc.).
   *  Built generically — any module can switch it on later. Default off. */
  @Input() enableMetadata: boolean = false;
  /** Optional fixed CSS height (e.g. '400px') for the media area so the
   *  gallery is a constant height and scrolls internally regardless of how
   *  many images it holds. Null = sizes to content (default; other hosts
   *  unaffected). */
  @Input() maxContentHeight: string | null = null;
  @Input() isLocked: boolean = false;
  @Input() downloadPermission: string = 'DONOTAPPLY';
  @Input() copyPermission: string = 'DONOTAPPLY';
  @Input() cutPermission: string = 'DONOTAPPLY';
  @Input() deletePermission: string = 'DONOTAPPLY';
  @Input() uploadPermission: string = 'DONOTAPPLY';

  // View state
  viewMode: 'grid' | 'list' = 'grid';
  filter: 'all' | 'photos' | 'videos' = 'all';
  searchTerm: string = '';
  /** '' = all deliveries; otherwise a delivery_code to filter by. */
  selectedDeliveryCode: string = '';
  sortBy: 'name' | 'date' | 'size' = 'date';
  sortOrder: 'asc' | 'desc' = 'desc';

  /** Distinct source delivery codes among loaded files (for the dropdown). */
  get deliveryFilterOptions(): string[] {
    const codes = this.mediaItems
      .map((m) => (m as any).delivery_code)
      .filter((c: any): c is string => typeof c === 'string' && c.length > 0);
    return Array.from(new Set(codes)).sort();
  }

  // Media items
  mediaItems: MediaItem[] = [];
  filteredMedia: MediaItem[] = [];
  selectedCount: number = 0;
  mediaCount: number = 0;

  // Separate folders and files from API
  folderItems: MediaItem[] = [];
  fileItems: MediaItem[] = [];

  // Folders (legacy - kept for compatibility)
  folders: Folder[] = [
    { id: 0, name: 'All Media', count: 0 }
  ];
  currentFolder: number = 0;
  showFolders: boolean = true;
  showFolderDropdown: boolean = false;
  isMobileView: boolean = false;

  // Breadcrumb navigation for folder hierarchy
  breadcrumbs: Array<{ id: number; name: string }> = [];
  currentFolderId: number | null = null;

  // Navigation history for back button functionality
  navigationHistory: Array<{ id: number; name: string }> = [];

  // Folder scrolling properties
  visibleFolderCount: number = 4;
  folderScrollPosition: number = 0;

  // Upload state
  isDragging: boolean = false;
  uploadProgress: number = 0;
  isUploading: boolean = false;

  // Context menu
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };
  contextMenuItem: MediaItem | null = null;

  // Copy/Cut clipboard
  copiedItem: MediaItem | null = null;
  copiedItemType: 'copy' | 'cut' | null = null;
  copiedItems: MediaItem[] = [];  // For bulk copy/cut operations

  // Loading state
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';

  // Track loaded record to prevent duplicate API calls
  private hasLoadedForRecord: number | null = null;

  // Gallery folder ID for the current record
  private galleryFolderId: number | null = null;

  // Store last known total count for All Media (from pagination.total)
  private lastPaginationTotal: number | null = null;

  // Shown during the 5s initial delay (queue job warmup)
  isInitialPreparing: boolean = false;
  // Shown after 404: folders still being created by the queue job
  isFoldersPreparingState: boolean = false;
  private readonly MAX_POLL_RETRIES = 3;
  private readonly POLL_INTERVAL_MS  = 6000;
  private readonly INITIAL_LOAD_DELAY_MS = 5000;
  private destroy$ = new Subject<void>();
  private pollingSubscription: Subscription | null = null;
  private initialLoadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private dialog: MatDialog,
    private documentService: DocumentService,
    private attachmentService: AttachmentsServiceService,
    private toasterService: ToasterService,
    private helperService: HelperService,
    private firebaseEventService: FirebaseEventService,
    private cdr: ChangeDetectorRef
  ) {}

  canDownload(): boolean {
    return this.helperService.isPermissionAvailable(this.downloadPermission);
  }

  canCopy(): boolean {
    return this.helperService.isPermissionAvailable(this.copyPermission);
  }

  canCut(): boolean {
    return this.helperService.isPermissionAvailable(this.cutPermission);
  }

  canDelete(): boolean {
    return this.helperService.isPermissionAvailable(this.deletePermission);
  }

  canUpload(): boolean {
    return this.helperService.isPermissionAvailable(this.uploadPermission);
  }

  ngOnInit(): void {
    if (this.recordId) {
      this.hasLoadedForRecord = this.recordId;
      // Show "Preparing..." during the 5s warmup, then switch to "Loading..." when the request fires
      this.isInitialPreparing = true;
      this.initialLoadTimer = setTimeout(() => {
        this.isInitialPreparing = false;
        this.loadMediaItems();
      }, this.INITIAL_LOAD_DELAY_MS);
    }
    this.checkMobileView();
    window.addEventListener('resize', () => this.checkMobileView());
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if recordId changed and is now available
    if (changes['recordId'] && changes['recordId'].currentValue) {
      // ngOnInit handles the initial load with a delay — only react to subsequent record changes here
      if (changes['recordId'].isFirstChange()) return;

      const newRecordId = changes['recordId'].currentValue;

      // Only load if this is a different record
      if (this.hasLoadedForRecord !== newRecordId) {
        console.log('Record ID changed to:', newRecordId);
        this.hasLoadedForRecord = newRecordId;
        this.loadMediaItems();
      }
    }
  }

  // Load media items from backend
  loadMediaItems(): void {
    if (!this.recordId) {
      console.warn('Cannot load media: recordId is required');
      return;
    }

    this.isLoading = true;
    this.hasError = false;

    // First, get the event folder for this record
    this.getEventFolderForGallery();
  }

  /**
   * Get or create the event folder for the gallery
   * This folder is specific to the 'save_image' event for the current bid/record
   */
  private getEventFolderForGallery(): void {
    const eventFolderParams = {
      module_id: this.moduleId,
      attachable_id: this.recordId,
      event: 'save_image'
    };

    this.documentService.getEventFolder(eventFolderParams).subscribe(
      (response: any) => {
        console.log('Event folder API response:', response);

        // Extract main_folder_id from nested response structure
        // Response structure: { data: { success, message, data: { id, ... } } }
        const mainFolderId = response?.data?.data?.id ||
                            response?.data?.data?.data?.id ||
                            response?.data?.folder_id ||
                            response?.folder_id;

        if (mainFolderId) {
          this.galleryFolderId = mainFolderId;
          console.log(`✅ Main folder ID retrieved: ${mainFolderId}`);

          // Now fetch main folders to display at top
          this.fetchMainFolders(mainFolderId);
        } else {
          console.warn('No folder_id returned from event-folder API. Response:', response);
          this.hasError = true;
          this.errorMessage = 'Gallery folder not found';
          this.isLoading = false;
        }
      },
      (error: any) => {
        // 404 means the event-folder endpoint doesn't exist yet
        // Gracefully show empty gallery instead of error state
        const status = error?.status || error?.error?.status;
        if (status === 404) {
          this.mediaItems = [];
          this.filteredMedia = [];
          this.folderItems = [];
          this.fileItems = [];
          this.isLoading = false;
          // Folders are being created by the queue job — show preparing state and wait
          this.isFoldersPreparingState = true;
          this.startFirebaseEventListener();
          this.startPolling();
          return;
        }
        console.error('Error getting event folder:', error);
        this.hasError = true;
        this.errorMessage = 'Failed to load gallery folder';
        this.isLoading = false;
      }
    );
  }

  // --- Preparing state: Firebase event listener + polling fallback ---

  private startFirebaseEventListener(): void {
    this.firebaseEventService.events
      .pipe(
        filter(event =>
          event.eventType === 'gallery_folder_ready' &&
          event.moduleId  === this.moduleId &&
          event.modelId   === this.recordId
        ),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.stopPolling();
        this.isFoldersPreparingState = false;
        this.loadMediaItems();
      });
  }

  private startPolling(): void {
    this.stopPolling();
    let attempts = 0;
    this.pollingSubscription = interval(this.POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$),
        take(this.MAX_POLL_RETRIES)
      )
      .subscribe(() => {
        attempts++;
        this.documentService.getEventFolder({
          module_id:     this.moduleId,
          attachable_id: this.recordId,
          event:         'save_image',
        }).subscribe({
          next: (response: any) => {
            const folderId = response?.data?.data?.id     ||
                             response?.data?.data?.data?.id ||
                             response?.data?.folder_id;
            if (folderId) {
              this.stopPolling();
              this.isFoldersPreparingState = false;
              this.loadMediaItems();
            }
          },
          error: () => {
            if (attempts >= this.MAX_POLL_RETRIES) {
              console.warn('Jobsite gallery: folder not ready after max polling retries.');
            }
          },
        });
      });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
    if (this.initialLoadTimer) {
      clearTimeout(this.initialLoadTimer);
      this.initialLoadTimer = null;
      this.isInitialPreparing = false;
    }
  }

  // --- End preparing state ---

  /**
   * Fetch main folders to display at the top
   * @param mainFolderId - The main folder ID from event-folder API
   */
  private fetchMainFolders(mainFolderId: number | null): void {
    const queryParam: any = {
      module_id: this.moduleId,
      folder_id: mainFolderId,
      page: 1,
      include:'children_count,files_count',
      // all_files: true
      // auto_parent: true
    };

    console.log('Fetching main folders with params:', queryParam);

    this.documentService.getDocuments(queryParam).subscribe(
      (response: any) => {
        console.log('Main folders API response:', response);

        // Process response to populate top folders
        this.processMainFoldersResponse(response);

        // After loading folders, automatically fetch "All Media" content
        // Since "All Media" (id: 0) is selected by default
        console.log('Auto-fetching All Media content on initial load');
        this.fetchAllMedia(mainFolderId);
      },
      (error: any) => {
        console.error('Error loading main folders:', error);
        this.hasError = true;
        this.errorMessage = 'Failed to load folders';
        this.isLoading = false;
      }
    );
  }

  /**
   * Process main folders response and populate folders array
   */
  private processMainFoldersResponse(response: any): void {
    let documentsArray = [];

    if (response?.data?.data?.documents) {
      const documentsData = response.data.data.documents;
      documentsArray = documentsData.list || [];
    } else if (response?.data && Array.isArray(response.data)) {
      documentsArray = response.data;
    } else if (Array.isArray(response)) {
      documentsArray = response;
    }

    // Filter only folders
    const mainFolders = documentsArray.filter((item: any) => item.type === 'folder');

    // Reset folders array with "All Media" at top
    this.folders = [{ id: 0, name: 'All Media', count: 0 }];

    // Add main folders from API
    mainFolders.forEach((folder: any) => {
      this.folders.push({
        id: folder.id,
        name: folder.original_name || folder.name || 'Untitled Folder',
        // Use children_count (fallback to files_count) for main folder counts
        count: (folder.children_count ?? folder.files_count ?? 0)
      });
    });

    console.log('Main folders populated:', this.folders);
  }

  /**
   * Fetch all media files when "All Media" is clicked
   * @param mainFolderId - The main folder ID from event-folder API
   */
  private fetchAllMedia(mainFolderId: number | null): void {
    // Common image extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico', 'heic', 'heif', 'raw', 'cr2', 'nef', 'arw'];

    // Common video extensions
    const videoExtensions = ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'mpeg', 'mpg', 'm4v', '3gp', 'ogv', 'vob', 'mts', 'm2ts'];

    // Combine all extensions
    const allExtensions = [...imageExtensions, ...videoExtensions];

    const queryParam: any = {
      module_id: this.moduleId,
      folder_id: mainFolderId,
      page: 1,
      // auto_parent: true,
      all_files: true,
      // file_type: allExtensions.join(','),
      include: 'children_count'
    };

    console.log('Fetching all media files with params:', queryParam);

    this.documentService.getDocuments(queryParam, false).subscribe(
      (response: any) => {
        // Set All Media count from pagination.total when available
        let paginationTotal: number | null = null;
        if (response?.data?.data?.documents?.pagination?.total != null) {
          paginationTotal = Number(response.data.data.documents.pagination.total);
        }
        this.lastPaginationTotal = paginationTotal;
        if (paginationTotal != null) {
          const allMediaTab = this.folders.find(f => f.id === 0);
          if (allMediaTab) {
            allMediaTab.count = paginationTotal;
          }
        }

        this.processMediaResponse(response);
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Error loading all media:', error);
        this.hasError = true;
        this.errorMessage = 'Failed to load media items';
        this.isLoading = false;
      }
    );
  }

  /**
   * Fetch documents for the specific gallery folder
   * @param folderId - The folder ID to fetch documents from
   * @param folderName - Optional folder name for breadcrumb
   */
  private fetchDocumentsForFolder(folderId: number, folderName: string = 'Gallery'): void {
    // Set current folder
    this.currentFolderId = folderId;

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif', 'ico', 'heic', 'heif', 'raw', 'cr2', 'nef', 'arw'];
    const videoExtensions = ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'mpeg', 'mpg', 'm4v', '3gp', 'ogv', 'vob', 'mts', 'm2ts'];
    const folderExtensions = ['folder'];
    const allExtensions = [...imageExtensions, ...videoExtensions];

    const queryParam: any = {
      module_id: this.moduleId,
      folder_id: folderId,
      page: 1,
      // auto_parent: true
      include:'children_count,files_count',
      // all_files: true,
      // file_type: allExtensions.join(',')
    };

    console.log('Fetching documents for folder:', folderId, 'with params:', queryParam);

    // Using document service to get media files and folders
    this.documentService.getDocuments(queryParam, false).subscribe(
      (response: any) => {
        this.processMediaResponse(response);
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Error loading media:', error);
        this.hasError = true;
        this.errorMessage = 'Failed to load media items';
        this.isLoading = false;
      }
    );
  }

  /**
   * Navigate into a folder
   * @param folder - The folder item to navigate into
   */
  public navigateToFolder(folder: MediaItem): void {
    if (folder.type !== 'folder') {
      console.warn('Item is not a folder');
      return;
    }

    console.log('Navigating to folder:', folder.name, folder.id);

    // Save current folder to navigation history before navigating
    if (this.currentFolderId) {
      const currentFolderName = this.getCurrentFolderName();
      this.navigationHistory.push({ id: this.currentFolderId, name: currentFolderName });
    }

    // Add to breadcrumbs
    this.breadcrumbs.push({ id: folder.id, name: folder.name });

    // Fetch contents of this folder
    this.isLoading = true;
    this.fetchDocumentsForFolder(folder.id, folder.name);
  }

  /**
   * Get the name of the current folder
   */
  private getCurrentFolderName(): string {
    // Check if we have breadcrumbs
    if (this.breadcrumbs.length > 0) {
      return this.breadcrumbs[this.breadcrumbs.length - 1].name;
    }
    // Fallback to finding folder in main folders
    const folder = this.folders.find(f => f.id === this.currentFolderId);
    return folder?.name || 'Folder';
  }

  /**
   * Navigate to a breadcrumb folder
   * @param breadcrumbIndex - Index in breadcrumb array
   */
  public navigateToBreadcrumb(breadcrumbIndex: number): void {
    const breadcrumb = this.breadcrumbs[breadcrumbIndex];
    if (!breadcrumb) return;

    console.log('Navigating via breadcrumb to:', breadcrumb.name);

    // Trim breadcrumbs to this level
    this.breadcrumbs = this.breadcrumbs.slice(0, breadcrumbIndex + 1);

    // Set currentFolderId for upload/create operations
    this.currentFolderId = breadcrumb.id;

    // Fetch contents of this folder
    this.isLoading = true;
    this.fetchDocumentsForFolder(breadcrumb.id, breadcrumb.name);
  }

  /**
   * Navigate to home (All Media)
   */
  public navigateToHome(): void {
    console.log('Navigating to home/All Media');

    // Clear breadcrumbs
    this.breadcrumbs = [];

    // Reset to All Media
    this.currentFolder = 0;
    this.currentFolderId = null;

    // Load all media
    this.isLoading = true;
    this.fetchAllMedia(this.galleryFolderId);
  }

  /**
   * Go back to parent folder using navigation history
   */
  public goBackToPreviousFolder(): void {
    if (this.breadcrumbs.length === 0) {
      console.log('No breadcrumb path available');
      return;
    }

    // Remove current folder from breadcrumbs
    this.breadcrumbs.pop();

    if (this.breadcrumbs.length > 0) {
      // Go to last folder in breadcrumbs
      const previousFolder = this.breadcrumbs[this.breadcrumbs.length - 1];
      console.log('Going back to folder:', previousFolder.name, previousFolder.id);

      // Set currentFolderId for upload/create operations
      this.currentFolderId = previousFolder.id;

      // Fetch the previous folder contents
      this.isLoading = true;
      this.fetchDocumentsForFolder(previousFolder.id, previousFolder.name);
    } else {
      // Go back to All Media
      console.log('Going back to All Media');
      this.currentFolder = 0;
      this.currentFolderId = null;

      // Load all media
      this.isLoading = true;
      this.fetchAllMedia(this.galleryFolderId);
    }

    // Update navigation history to match
    this.navigationHistory = this.breadcrumbs.slice(0, -1);
  }

  /**
   * Check if back button should be shown
   */
  public shouldShowBackButton(): boolean {
    return this.breadcrumbs.length > 0;
  }

  // Process API response
  private processMediaResponse(response: any): void {
    // Handle ProSuite's nested response structure
    let documentsArray = [];

    if (response?.data?.data?.documents) {
      // ProSuite standard structure
      const documentsData = response.data.data.documents;
      documentsArray = documentsData.list || [];
    } else if (response?.data && Array.isArray(response.data)) {
      documentsArray = response.data;
    } else if (Array.isArray(response)) {
      documentsArray = response;
    }

    // Separate folders from files
    const folders = documentsArray.filter((item: any) => item.type === 'folder');
    const files = documentsArray.filter((item: any) => item.type === 'file' || !item.type);

    // Process folders
    this.folderItems = folders.map((item: any) => {
      const creatorName = item.creator?.full_name || item.created_by_name || item.user_name || 'Unknown User';
      return {
        id: item.id,
        name: item.original_name || item.name || 'Untitled Folder',
        type: 'folder',
        url: '',
        thumbnail_url: '',
        file_type: 'folder',
        size: 0,
        date: new Date(item.created_at || item.date),
        folder_id: item.folder_id,
        document_id: item.document_id,
        tags: [],
        description: '',
        selected: false,
        editing: false,
        is_primary: false,
        uploaded_by: creatorName,
        user_avatar: item.user_avatar || item.avatar_url || item.created_by_avatar || '',
        user_initials: this.getUserInitials(creatorName)
      };
    });

    // Process ALL files (not just images/videos - include pdf, etc.)
    this.fileItems = files.map((item: any) => {
      const creatorName = item.creator?.full_name || item.created_by_name || item.uploaded_by || item.user_name || 'Unknown User';
      return {
        id: item.id,
        name: item.original_name || item.name || 'Untitled',
        type: 'file',
        url: item.url || item.file_url,
        thumbnail_url: item.thumbnail_url || item.url || item.file_url,
        file_type: (item.file_extension || item.file_type || '').toLowerCase(),
        size: item.file_size || 0,
        date: new Date(item.created_at || item.date),
        folder_id: item.folder_id || 0,
        document_id: item.document_id,
        // Carry saved markup through so the annotator can preload it on open.
        annotations: item.annotations ?? item.markup?.annotations ?? [],
        comments: item.comments ?? item.markup?.comments ?? [],
        tags: item.tags || [],
        description: item.description || '',
        selected: false,
        editing: false,
        is_primary: item.is_primary || false,
        uploaded_by: creatorName,
        user_avatar: item.user_avatar || item.avatar_url || item.created_by_avatar || '',
        user_initials: this.getUserInitials(creatorName),
        // Source delivery (warehouse files mirrored from a delivery); null
        // for everything else. Powers the optional "filter by delivery" and
        // lets the search match by delivery / vendor / PO / job name.
        delivery_code: item.delivery_code || null,
        vendor_name: item.vendor_name || null,
        po_number: item.po_number || null,
        job_name: item.job_name || null,
      };
    });

    // Combine folders and files for display in gallery-container
    // Folders first, then files
    this.mediaItems = [...this.folderItems, ...this.fileItems];
    this.mediaCount = this.mediaItems.length;

    console.log('Processed media response:', {
      totalItems: this.mediaItems.length,
      folders: this.folderItems.length,
      files: this.fileItems.length
    });

    this.updateFolderCounts();
    this.applyFilters();
  }

  // Get user initials from name
  private getUserInitials(name: string): string {
    if (!name || name === 'Unknown') return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  // Get avatar background color based on name
  getAvatarColor(name: string): string {
    const colors = [
      '#ff6600', // Orange (primary)
      '#28304e', // Blue
      '#66bb6a', // Green
      '#ffa726', // Amber
      '#ab47bc', // Purple
      '#29b6f6', // Light Blue
      '#ff7043', // Deep Orange
      '#8d6e63'  // Brown
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  // Update folder counts
  private updateFolderCounts(): void {
    this.folders[0].count = this.mediaItems.length;
    // for (let i = 1; i < this.folders.length; i++) {
    //   this.folders[i].count = this.mediaItems.filter(item => item.folder_id === i).length;
    // }
  }

  // Apply filters and search
  applyFilters(): void {
    let items = [...this.mediaItems];

    // Note: Do NOT filter by folder_id here
    // When a folder is clicked, fetchDocumentsForFolder already fetches
    // the correct files for that folder. The items in mediaItems are
    // already the contents of the selected folder.

    // Filter by type
    if (this.filter === 'photos') {
      items = items.filter(item => this.isImage(item));
    } else if (this.filter === 'videos') {
      items = items.filter(item => this.isVideo(item));
    }

    // Search — file name / description / tags, plus (for warehouse files
    // mirrored from a delivery) the delivery code, vendor, PO and job name.
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      const has = (v?: string | null) => !!v && v.toLowerCase().includes(term);
      items = items.filter(item =>
        item.name.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.tags?.some(tag => tag.toLowerCase().includes(term)) ||
        has(item.delivery_code) ||
        has(item.vendor_name) ||
        has(item.po_number) ||
        has(item.job_name)
      );
    }

    // Warehouse-only: filter by source delivery.
    if (this.enableDeliveryFilter && this.selectedDeliveryCode) {
      items = items.filter(item => (item as any).delivery_code === this.selectedDeliveryCode);
    }

    // Sort
    items.sort((a, b) => {
      let comparison = 0;
      switch (this.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'date':
          comparison = a.date.getTime() - b.date.getTime();
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
      }
      return this.sortOrder === 'asc' ? comparison : -comparison;
    });

    this.filteredMedia = items;

    console.log('Applied filters:', {
      totalMediaItems: this.mediaItems.length,
      filteredCount: this.filteredMedia.length,
      currentFolder: this.currentFolder,
      filter: this.filter,
      searchTerm: this.searchTerm
    });
  }

  // Check if item is image
  isImage(item: MediaItem): boolean {
    return this.attachmentService.isImage(item.file_type);
  }

  // Check if item is video
  isVideo(item: MediaItem): boolean {
    const videoTypes = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
    return videoTypes.includes(item.file_type.toLowerCase());
  }

  // Toggle view mode
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  // Set filter
  setFilter(filter: 'all' | 'photos' | 'videos'): void {
    this.filter = filter;
    this.applyFilters();
  }


  // Select/deselect item
  selectItem(item: MediaItem, event: MouseEvent): void {
    if (!event.ctrlKey && !event.shiftKey) {
      // Clear other selections
      this.mediaItems.forEach(i => i.selected = false);
    }
    item.selected = !item.selected;
    this.updateSelectedCount();
  }

  // Update selected count
  updateSelectedCount(): void {
    this.selectedCount = this.mediaItems.filter(item => item.selected).length;
  }

  // Toggle all items selection
  toggleAllSelection(event: any): void {
    const checked = event.checked;
    this.filteredMedia.forEach(item => item.selected = checked);
    this.updateSelectedCount();
  }

  // Preview media item
  preview(item: MediaItem): void {
    if (this.isImage(item)) {
      // Open the new document image annotator (viewer + annotate/comment + export).
      // These gallery items are document records, so item.id is a real documents.id →
      // markup persists via the annotator. __sourceRef keeps the list item in sync on
      // save so a same-session reopen shows the latest markup.
      this.dialog.open(DocumentImageAnnotatorComponent, {
        width: '92vw',
        maxWidth: '92vw',
        height: '88vh',
        data: {
          url: item.url,
          id: item.id,
          original_name: item.name,
          file_type: item.file_type,
          annotations: item.annotations ?? [],
          comments: item.comments ?? [],
          __sourceRef: item
        },
        panelClass: 'document-image-annotator-panel'
      });
    } else if (this.isVideo(item)) {
      // Open video in new tab or custom video player
      window.open(item.url, '_blank');
    }
  }

  /** Human label for the host module the gallery is mounted on. */
  private moduleLabel(): string {
    const map: { [key: number]: string } = {
      3: 'Bid',
      13: 'Customer',
      16: 'Bid',
      17: 'Warehouse',
      19: 'Job'
    };
    return map[this.moduleId] || 'Record';
  }

  /** Build the lightbox detail rows ({icon,label,value}) for an image. */
  private buildPreviewMeta(item: MediaItem): Array<{ icon?: string; label: string; value: string }> {
    const rows: Array<{ icon?: string; label: string; value: string }> = [];

    if (item.uploaded_by) {
      rows.push({ icon: 'fa-user', label: 'Uploaded by', value: item.uploaded_by });
    }
    if (item.date) {
      rows.push({ icon: 'fa-clock', label: 'Uploaded', value: this.formatMetaDate(item.date) });
    }
    rows.push({ icon: 'fa-layer-group', label: 'Module', value: this.moduleLabel() });
    if (item.delivery_code) {
      rows.push({ icon: 'fa-truck', label: 'Delivery', value: item.delivery_code });
    }
    if (item.file_type) {
      rows.push({ icon: 'fa-file', label: 'Type', value: item.file_type.toUpperCase() });
    }
    const sizeKb = Number(item.size);
    if (sizeKb > 0) {
      rows.push({ icon: 'fa-hdd', label: 'Size', value: this.formatMetaSize(sizeKb) });
    }
    if (item.is_primary) {
      rows.push({ icon: 'fa-star', label: 'Primary', value: 'Yes' });
    }

    return rows;
  }

  private formatMetaDate(value: Date | string): string {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  /** File sizes are stored in KB by the document/gallery backend. */
  private formatMetaSize(sizeKb: number): string {
    if (!sizeKb || sizeKb <= 0) {
      return '—';
    }
    if (sizeKb >= 1024) {
      return `${Math.round((sizeKb / 1024) * 100) / 100} MB`;
    }
    return `${Math.round(sizeKb * 100) / 100} KB`;
  }

  // Edit item details
  editItem(item: MediaItem): void {
    item.editing = true;
  }

  // Save item name
  saveItemName(item: MediaItem, event: any): void {
    const newName = event.target.textContent.trim();
    if (newName && newName !== item.name) {
      item.name = newName;
      this.updateItemMetadata(item);
    }
    item.editing = false;
  }

  // Update item metadata
  private updateItemMetadata(item: MediaItem): void {
    // TODO: Implement update when API is available
    console.log('Update metadata:', item);
    this.toasterService.showInfo('Update functionality coming soon');
  }

  // Download item - opens in new tab
  download(item: MediaItem): void {
    if (!this.canDownload()) {
      return;
    }
    window.open(item.url, '_blank');
  }

  // Delete item
  deleteItem(item: MediaItem): void {
    if (this.isLocked || !this.canDelete()) {
      return;
    }
    if (confirm(`Are you sure you want to delete ${item.name}?`)) {
      this.documentService.deleteDocument(item.id).subscribe(
        (response: any) => {
          this.toasterService.showSuccessMessage(response?.data?.message);
          this.mediaItems = this.mediaItems.filter(i => i.id !== item.id);
          this.applyFilters();
          this.updateFolderCounts();
        },
        (error: any) => {
          console.error('Error deleting item:', error);
          const itemType = item.type === 'folder' ? 'folder' : 'file';
          this.toasterService.showError(`Failed to delete ${itemType}`);
        }
      );
    }
  }

  // Set as primary image
  setAsPrimary(item: MediaItem): void {
    // Clear other primary flags
    this.mediaItems.forEach(i => i.is_primary = false);
    item.is_primary = true;
    this.updateItemMetadata(item);
  }

  // Bulk operations
  bulkDownload(): void {
    if (!this.canDownload()) {
      return;
    }
    const selected = this.mediaItems.filter(item => item.selected);
    if (selected.length === 1) {
      this.download(selected[0]);
    }
  }

  /**
   * Copy selected items to clipboard
   */
  bulkCopy(): void {
    if (!this.canCopy()) {
      return;
    }
    const selected = this.mediaItems.filter(item => item.selected);
    if (selected.length === 0) {
      return;
    }

    this.copiedItems = [...selected];
    this.copiedItemType = 'copy';

    // For backward compatibility, also set single item
    this.copiedItem = selected[0];

    // Show success message
    this.toasterService.showSuccessMessage(`${selected.length} item(s) copied to clipboard`);

    // Deselect all items to hide the toolbar
    this.mediaItems.forEach(i => i.selected = false);
    this.updateSelectedCount();
  }

  /**
   * Cut selected items to clipboard for moving
   */
  bulkMove(): void {
    if (this.isLocked || !this.canCut()) {
      return;
    }
    const selected = this.mediaItems.filter(item => item.selected);
    if (selected.length === 0) {
      return;
    }

    this.copiedItems = [...selected];
    this.copiedItemType = 'cut';

    // For backward compatibility, also set single item
    this.copiedItem = selected[0];

    // Show success message
    this.toasterService.showSuccessMessage(`${selected.length} item(s) ready to move`);

    // Deselect all items to hide the toolbar
    this.mediaItems.forEach(i => i.selected = false);
    this.updateSelectedCount();
  }

  /**
   * Paste copied/moved items to current folder
   */
  bulkPaste(): void {
    if (this.isLocked) {
      return;
    }
    if (!this.copiedItems || this.copiedItems.length === 0) {
      this.toasterService.showWarning('No items in clipboard');
      return;
    }

    const destinationFolderId = this.currentFolderId || this.galleryFolderId;

    if (!destinationFolderId) {
      this.toasterService.showWarning('No destination folder selected');
      return;
    }

    // Use bulk API if multiple items, otherwise use single item API
    if (this.copiedItems.length > 1) {
      const bulkData = {
        copy_folder_ids: this.copiedItems.map(item => item.id),
        past_folder_id: destinationFolderId,
        action: this.copiedItemType as 'copy' | 'cut'
      };

      console.log('Bulk pasting items:', bulkData);

      this.documentService.bulkPostAction(bulkData).subscribe(
        (response: any) => {
          // this.toasterService.showSuccess(`${this.copiedItems.length} item(s) ${this.copiedItemType === 'copy' ? 'copied' : 'moved'} successfully`);
          this.toasterService.showSuccessMessage(response?.data?.message);
          // Clear clipboard after paste
          this.copiedItems = [];
          this.copiedItem = null;
          this.copiedItemType = null;

          // Refresh current view
          this.refreshCurrentView();
        },
        (error: any) => {
          console.error('Bulk paste error:', error);
          this.toasterService.showError('Failed to paste items');
        }
      );
    } else {
      // Single item paste (backward compatibility)
      const pasteData = {
        copy_folder_id: this.copiedItems[0].id,
        past_folder_id: destinationFolderId,
        action: this.copiedItemType
      };

      console.log('Pasting single item:', pasteData);

      this.documentService.pasteDocument(pasteData).subscribe(
        (response: any) => {
          this.toasterService.showSuccessMessage(`Item ${this.copiedItemType === 'copy' ? 'copied' : 'moved'} successfully`);

          // Clear clipboard after paste
          this.copiedItems = [];
          this.copiedItem = null;
          this.copiedItemType = null;

          // Refresh current view
          this.refreshCurrentView();
        },
        (error: any) => {
          console.error('Paste error:', error);
          this.toasterService.showError('Failed to paste item');
        }
      );
    }
  }

  /**
   * Delete selected items
   */
  bulkDelete(): void {
    if (this.isLocked || !this.canDelete()) {
      return;
    }
    const selected = this.mediaItems.filter(item => item.selected);
    if (selected.length === 0) {
      return;
    }

    const itemText = selected.length === 1
      ? `"${selected[0].name}"`
      : `${selected.length} items`;

    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '500px',
      height: '300px',
      data: {
        title: `Delete ${itemText}`,
        message: `Are you sure you want to delete ${itemText}?`,
        description: 'This action cannot be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        isDanger: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Use bulk delete API if multiple items
        if (selected.length > 1) {
          const bulkData = {
            folder_ids: selected.map(item => item.id)
          };

          this.documentService.bulkDelete(bulkData).subscribe(
            (response: any) => {
              this.toasterService.showSuccessMessage(response?.data?.message);
              this.refreshCurrentView();
            },
            (error: any) => {
              console.error('Bulk delete error:', error);
              this.toasterService.showError('Failed to delete items');
            }
          );
        } else {
          // Single item delete (backward compatibility)
          this.documentService.deleteDocument(selected[0].id).subscribe(
            (response: any) => {
              this.toasterService.showSuccessMessage(response?.data?.message);
              this.refreshCurrentView();
            },
            (error: any) => {
              console.error('Delete error:', error);
              const itemType = selected[0].type === 'folder' ? 'folder' : 'file';
              this.toasterService.showError(`Failed to delete ${itemType}`);
            }
          );
        }
      }
    });
  }

  /**
   * Refresh the current view after copy/move/delete operations
   */
  private refreshCurrentView(): void {
    // Always refresh main folders to update counts
    if (this.galleryFolderId) {
      this.fetchMainFolders(this.galleryFolderId);
    }

    if (this.currentFolderId) {
      // Reload current folder contents
      const currentFolder = this.folders.find(f => f.id === this.currentFolder);
      if (currentFolder && currentFolder.id > 0) {
        this.fetchDocumentsForFolder(this.currentFolderId, currentFolder.name);
      } else {
        this.fetchAllMedia(this.galleryFolderId);
      }
    } else if (this.currentFolder === 0) {
      // Reload All Media
      this.fetchAllMedia(this.galleryFolderId);
    } else {
      // Reload main folders
      this.fetchMainFolders(this.galleryFolderId);
    }
  }

  /**
   * Check if actions should be enabled (for one or more selections)
   */
  hasItemsSelected(): boolean {
    return this.selectedCount > 0;
  }

  /**
   * Check if paste action should be enabled
   */
  canPaste(): boolean {
    return this.copiedItems.length > 0 || this.copiedItem !== null;
  }

  /**
   * Get clipboard item count for display
   */
  getClipboardItemCount(): number {
    return this.copiedItems.length;
  }

  /**
   * Get clipboard display text
   */
  getClipboardDisplayText(): string {
    if (this.copiedItems.length === 0) return '';
    if (this.copiedItems.length === 1) {
      const name = this.copiedItems[0].name;
      return name.length > 10 ? name.substring(0, 10) + '...' : name;
    }
    return `${this.copiedItems.length} items`;
  }

  /**
   * Close the toolbar by deselecting all items
   */
  closeToolbar(): void {
    this.mediaItems.forEach(i => i.selected = false);
    this.updateSelectedCount();
  }

  // Create new folder
  createFolder(): void {
    if (this.isLocked) {
      return;
    }
    // Ensure we have the gallery folder ID before creating a folder
    if (!this.galleryFolderId) {
      this.toasterService.showWarning('Gallery folder not ready. Please wait...');
      return;
    }

    // Determine the parent folder ID
    // If user is inside a specific folder, create subfolder there
    // Otherwise, create in the main gallery folder
    const parentFolderId = this.currentFolderId || this.galleryFolderId;

    const dialogRef = this.dialog.open(DocumentCreateFolderComponent, {
      width: '500px',
      maxHeight: '80vh',
      panelClass: 'create-folder-dialog-container',
      data: {
        module_id: this.moduleId,
        folder_id: parentFolderId,
        scope_id: this.recordId,
        type: 'main'
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        console.log('Folder created successfully:', result);
    
        // Always refresh main folders first to update the tabs
        this.fetchMainFolders(this.galleryFolderId);
    
        // Then refresh the current view
        if (this.currentFolderId) {
          // Reload current folder contents
          const currentFolder = this.folders.find(f => f.id === this.currentFolder);
          if (currentFolder && currentFolder.id > 0) {
            this.fetchDocumentsForFolder(this.currentFolderId, currentFolder.name);
          } else {
            this.fetchAllMedia(this.galleryFolderId);
          }
        } else if (this.currentFolder === 0) {
          // Reload All Media
          this.fetchAllMedia(this.galleryFolderId);
        }
      }
    });
  }

  // Drag and drop handlers
  onDrop(event: CdkDragDrop<MediaItem[]>): void {
    moveItemInArray(this.filteredMedia, event.previousIndex, event.currentIndex);
  }

  onDropToFolder(event: any, folder: Folder): void {
    // Move item to folder
    const item = event.item.data as MediaItem;
    item.folder_id = folder.id;
    this.updateItemMetadata(item);
    this.updateFolderCounts();
    this.applyFilters();
  }

  /**
   * ==========================================================================
   * DRAG & DROP MEDIA UPLOAD
   * --------------------------------------------------------------------------
   * Ported from Task Attachments (and shared app-module-documents) so the
   * behaviour matches exactly: dragCounter-guarded overlay, upload queue,
   * validation, 3-file concurrency, progress panel, success/failure handling.
   * Adapted only for gallery context: media-only types, attachment_field
   * 'image', and the gallery's own folder/refresh logic.
   *
   * The existing openUploadDialog() / Upload Media buttons are untouched.
   * ==========================================================================
   */

  /** Depth counter so the overlay does not flicker over child elements. */
  dragCounter = 0;
  /** File count reported by the drag, shown in the overlay badge. */
  draggedFileCount = 0;

  /** Upload queue — same shape as Task Attachments' UploadFile. */
  uploadQueue: any[] = [];
  /** Template alias for the queue, matching Task Attachments. */
  uploadProgressList: any[] = [];
  /** Whether the progress panel body is expanded. */
  isUploadPanelOpen = true;
  /** Active upload subscriptions, keyed by queue item id (for cancellation). */
  private currentUploads = new Map<string, any>();

  MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB — same as Task
  MAX_CONCURRENT_UPLOADS = 3; // same as Task
  /** Media-only: the gallery holds photos and videos, not documents. */
  ALLOWED_TYPES = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'heic', 'heif', // Images
    'mp4', 'mov', 'avi', 'mkv', 'webm', // Video
  ];

  /** Reuses the gallery's existing lock + permission gate, unchanged. */
  canDropFiles(): boolean {
    return !this.isLocked && this.canUpload();
  }

  onDragEnter(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    event.preventDefault();
    this.dragCounter++;
    this.isDragging = true;

    if (event.dataTransfer?.items) {
      this.draggedFileCount = event.dataTransfer.items.length;
    }
  }

  onDragOver(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    event.preventDefault();
    this.dragCounter--;

    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragging = false;
      this.draggedFileCount = 0;
    }
  }

  /** Drop handler — clears drag state inline and synchronously, as Task does. */
  onFileDrop(event: DragEvent): void {
    if (!this.canDropFiles()) return;
    event.preventDefault();

    this.isDragging = false;
    this.dragCounter = 0;
    this.draggedFileCount = 0;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files);
    }
  }

  /**
   * Safety net: clear the overlay when a drag ends anywhere in the window, so
   * a swallowed dragleave cannot leave the overlay covering the gallery.
   */
  @HostListener('window:drop')
  @HostListener('window:dragend')
  @HostListener('window:dragleave', ['$event'])
  onWindowDragEnd(event?: DragEvent): void {
    if (event && event.relatedTarget !== null) {
      return;
    }
    if (!this.isDragging && this.dragCounter === 0) return;
    this.isDragging = false;
    this.dragCounter = 0;
    this.draggedFileCount = 0;
  }

  /** Validate, queue and start uploads — same flow as Task. */
  handleFileUpload(files: FileList | File[]): void {
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      const validation = this.validateFile(file);
      if (!validation.valid) {
        this.toasterService.showError('', validation.error || 'Invalid file');
        continue;
      }

      if (this.isDuplicateFile(file)) {
        this.toasterService.showWarning('', `${file.name} is already in upload queue`);
        continue;
      }

      const queueItem: any = {
        id: this.generateUploadUUID(),
        file: file,
        fileName: file.name,
        fileSize: this.formatUploadFileSize(file.size),
        fileSizeBytes: file.size,
        fileType: file.name.split('.').pop()?.toLowerCase() || '',
        status: 'pending',
        progress: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
        preview: undefined,
        retryCount: 0,
      };

      this.uploadQueue.push(queueItem);

      // Thumbnail is generated AFTER queueing and never awaited before it:
      // Zone.js treats FileReader as a macrotask, so awaiting it here would
      // keep the zone unstable and leave the drag overlay painted on screen.
      this.generateUploadPreview(file).then(preview => {
        queueItem.preview = preview;
      });
    }

    this.uploadProgressList = this.uploadQueue;
    this.isUploadPanelOpen = true;

    this.processUploadQueue();
  }

  /** Media-only validation: size cap then image/video extension whitelist. */
  private validateFile(file: File): { valid: boolean; error?: string } {
    if (file.size > this.MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds ${this.formatUploadFileSize(this.MAX_FILE_SIZE)} limit`,
      };
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !this.ALLOWED_TYPES.includes(ext)) {
      return { valid: false, error: `Only photos and videos can be added to the gallery (.${ext} is not supported)` };
    }

    return { valid: true };
  }

  /** Same duplicate rule as Task: name + byte size still in flight. */
  private isDuplicateFile(file: File): boolean {
    return this.uploadQueue.some(
      item =>
        item.fileName === file.name &&
        item.fileSizeBytes === file.size &&
        item.status !== 'error' &&
        item.status !== 'cancelled'
    );
  }

  private generateUploadPreview(file: File): Promise<string | undefined> {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(undefined);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(file);
    });
  }

  private generateUploadUUID(): string {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  formatUploadFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  formatUploadSpeed(bytesPerSecond: number): string {
    if (!bytesPerSecond) return '0 KB/s';
    return `${this.formatUploadFileSize(bytesPerSecond)}/s`;
  }

  formatTimeRemaining(seconds: number): string {
    if (!seconds || !isFinite(seconds)) return '';
    if (seconds < 60) return `${Math.ceil(seconds)}s left`;
    return `${Math.ceil(seconds / 60)}m left`;
  }

  /** Process upload queue with concurrent limit — same as Task. */
  private processUploadQueue(): void {
    const pending = this.uploadQueue.filter(f => f.status === 'pending');
    const uploading = this.uploadQueue.filter(f => f.status === 'uploading').length;

    const available = this.MAX_CONCURRENT_UPLOADS - uploading;
    const toStart = pending.slice(0, available);

    toStart.forEach(uploadFile => this.startUpload(uploadFile));
  }

  /** Start an individual upload against this gallery's media folder. */
  private startUpload(uploadFile: any): void {
    // Same folder resolution the gallery's upload dialog uses.
    const folderId = this.currentFolderId || this.galleryFolderId;

    if (!folderId) {
      this.handleUploadError(uploadFile, { message: 'Gallery folder not ready' });
      return;
    }

    uploadFile.status = 'uploading';
    uploadFile.startTime = Date.now();

    const uploadParams = {
      module_id: this.moduleId,
      folder_id: folderId as number,
      attachable_id: this.recordId ? Number(this.recordId) : undefined,
      attachment_field: 'image',
    };

    const uploadSubscription = this.documentService
      .uploadFileWithProgress(uploadFile.file, uploadParams, false)
      .pipe(
        catchError((error) => {
          this.handleUploadError(uploadFile, error);
          return EMPTY;
        })
      )
      .subscribe((event: HttpEvent<any>) => {
        this.handleUploadEvent(uploadFile, event);
      });

    this.currentUploads.set(uploadFile.id, uploadSubscription);
  }

  /** Handle upload progress events — same maths as Task. */
  private handleUploadEvent(uploadFile: any, event: HttpEvent<any>): void {
    if (event.type === HttpEventType.UploadProgress && event.total) {
      uploadFile.uploadedBytes = event.loaded;
      uploadFile.totalBytes = event.total;
      uploadFile.progress = Math.round((100 * event.loaded) / event.total);

      const elapsedTime = (Date.now() - (uploadFile.startTime || Date.now())) / 1000;
      uploadFile.uploadSpeed = elapsedTime > 0 ? event.loaded / elapsedTime : 0;

      const remainingBytes = event.total - event.loaded;
      uploadFile.timeRemaining =
        uploadFile.uploadSpeed > 0 ? remainingBytes / uploadFile.uploadSpeed : 0;

      this.cdr.detectChanges();
    } else if (event.type === HttpEventType.Response) {
      this.handleUploadComplete(uploadFile, event.body);
    }
  }

  /** Handle successful upload — gallery refreshes independently afterwards. */
  private handleUploadComplete(uploadFile: any, response: any): void {
    uploadFile.status = 'complete';
    uploadFile.progress = 100;
    uploadFile.endTime = Date.now();
    uploadFile.uploadedId = response?.data?.id;

    this.currentUploads.delete(uploadFile.id);

    this.toasterService.showSuccess('', `${uploadFile.fileName} uploaded successfully`);

    // Paint the completed state before the refresh round-trip.
    this.cdr.detectChanges();

    // Gallery's own existing refresh, unchanged.
    this.fetchMainFolders(this.galleryFolderId);
    this.loadMediaItems();

    this.processUploadQueue();
  }

  /** Handle upload error — same as Task. */
  private handleUploadError(uploadFile: any, error: any): void {
    uploadFile.status = 'error';
    uploadFile.error = error?.message || 'Upload failed';

    this.currentUploads.delete(uploadFile.id);

    this.toasterService.showError('', `Failed to upload ${uploadFile.fileName}`);
    this.cdr.detectChanges();

    this.processUploadQueue();
  }

  cancelUpload(uploadFile: any): void {
    const subscription = this.currentUploads.get(uploadFile.id);
    if (subscription) {
      subscription.unsubscribe();
      this.currentUploads.delete(uploadFile.id);
    }
    uploadFile.status = 'cancelled';
    this.processUploadQueue();
  }

  retryUpload(uploadFile: any): void {
    uploadFile.status = 'pending';
    uploadFile.progress = 0;
    uploadFile.error = undefined;
    uploadFile.retryCount = (uploadFile.retryCount || 0) + 1;
    this.processUploadQueue();
  }

  removeFromQueue(uploadFile: any): void {
    this.uploadQueue = this.uploadQueue.filter(f => f.id !== uploadFile.id);
    this.uploadProgressList = this.uploadQueue;
  }

  getUploadingCount(): number {
    return this.uploadQueue.filter(f => f.status === 'uploading').length;
  }

  hasCompletedUploads(): boolean {
    return this.uploadQueue.some(f => f.status === 'complete');
  }

  toggleUploadPanel(): void {
    this.isUploadPanelOpen = !this.isUploadPanelOpen;
  }

  clearCompleted(): void {
    this.uploadQueue = this.uploadQueue.filter(f => f.status !== 'complete');
    this.uploadProgressList = this.uploadQueue;
  }

  clearAll(): void {
    this.uploadQueue.forEach(f => {
      if (f.status === 'uploading') {
        this.currentUploads.get(f.id)?.unsubscribe();
      }
    });
    this.currentUploads.clear();
    this.uploadQueue = [];
    this.uploadProgressList = [];
  }


  openUploadDialog(): void {
    if (this.isLocked || !this.canUpload()) {
      return;
    }
    // Ensure we have the gallery folder ID before opening upload dialog
    if (!this.galleryFolderId) {
      this.toasterService.showWarning('Gallery folder not ready. Please wait...');
      return;
    }

    // Use current folder if user is inside a specific folder, otherwise use main gallery folder
    const targetFolderId = this.currentFolderId || this.galleryFolderId;

    const dialogRef = this.dialog.open(MediaUploadDialogComponent, {
      data: {
        module_id: this.moduleId,
        recordId: this.recordId,
        folder_id: targetFolderId,  // Upload to current folder
        type: 'media'  // Gallery uploads media files
      },
      width: '800px',
      maxHeight: '80vh',
      panelClass: 'media-upload-dialog-container',
      disableClose: true  // Prevent closing by clicking outside or pressing ESC
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        this.fetchMainFolders(this.galleryFolderId);
        // Refresh the current folder view after successful upload
        if (this.currentFolderId) {
          // Reload current folder contents
          const currentFolder = this.folders.find(f => f.id === this.currentFolder);
          if (currentFolder && currentFolder.id > 0) {
            this.fetchDocumentsForFolder(this.currentFolderId, currentFolder.name);
          } else {
            // Reload All Media
            this.fetchAllMedia(this.galleryFolderId);
          }
        } else {
          // Reload All Media by default
          this.fetchAllMedia(this.galleryFolderId);
        }
      }
    });
  }

  private uploadFiles(files: FileList): void {
    if (!this.canUpload()) {
      return;
    }
    // Ensure we have the gallery folder ID before opening upload dialog
    if (!this.galleryFolderId) {
      this.toasterService.showWarning('Gallery folder not ready. Please wait...');
      return;
    }

    // Open the upload dialog with the files
    const dialogRef = this.dialog.open(MediaUploadDialogComponent, {
      data: {
        module_id: this.moduleId,
        recordId: this.recordId,
        folder_id: this.galleryFolderId,  // Use gallery folder ID instead of currentFolder
        files: files,
        type: 'media'  // Gallery uploads media files
      },
      width: '800px',
      maxHeight: '80vh',
      panelClass: 'media-upload-dialog-container'
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
       
        this.fetchMainFolders(this.galleryFolderId);
         // Refresh the gallery after successful upload
        this.loadMediaItems();
      }
    });
  }

  // Context menu
  onContextMenu(event: MouseEvent, item: MediaItem): void {
    event.preventDefault();
    this.contextMenuItem = item;
    this.contextMenuPosition = { x: event.clientX, y: event.clientY };
    this.showContextMenu = true;
  }

  moveToFolder(item: MediaItem): void {
    // Implement folder selection dialog
    console.log('Move to folder:', item);
  }

  // Close context menu when clicking outside
  onDocumentClick(event: MouseEvent): void {
    if (this.showContextMenu) {
      this.showContextMenu = false;
    }
    if (this.showFolderDropdown) {
      this.showFolderDropdown = false;
    }
  }

  // Check if mobile view
  private checkMobileView(): void {
    this.isMobileView = window.innerWidth < 768;

    // Adjust visible folder count based on screen size
    if (window.innerWidth < 768) {
      this.visibleFolderCount = 3; // Mobile: show 3 folders
    } else if (window.innerWidth < 1200) {
      this.visibleFolderCount = 4; // Tablet: show 4 folders
    } else {
      this.visibleFolderCount = 4; // Desktop: show 4 folders
    }

    // Ensure scroll position is valid after resize
    const maxScroll = Math.max(0, this.folders.length - this.visibleFolderCount);
    this.folderScrollPosition = Math.min(this.folderScrollPosition, maxScroll);
  }

  // Toggle folder dropdown
  toggleFolderDropdown(): void {
    this.showFolderDropdown = !this.showFolderDropdown;
  }

  // Get folder name by ID
  getFolderName(folderId: number): string {
    const folder = this.folders.find(f => f.id === folderId);
    return folder ? folder.name : 'All Media';
  }

  // Get folder count by ID
  getFolderCount(folderId: number): number {
    const folder = this.folders.find(f => f.id === folderId);
    return folder ? folder.count : 0;
  }

  // Folder scrolling methods
  get visibleFolders(): Folder[] {
    const start = this.folderScrollPosition;
    const end = start + this.visibleFolderCount;
    return this.folders.slice(start, end);
  }

  canScrollLeft(): boolean {
    return this.folderScrollPosition > 0;
  }

  canScrollRight(): boolean {
    return this.folderScrollPosition + this.visibleFolderCount < this.folders.length;
  }

  scrollFoldersLeft(): void {
    if (this.canScrollLeft()) {
      this.folderScrollPosition = Math.max(0, this.folderScrollPosition - 1);
    }
  }

  scrollFoldersRight(): void {
    if (this.canScrollRight()) {
      const maxScroll = Math.max(0, this.folders.length - this.visibleFolderCount);
      this.folderScrollPosition = Math.min(maxScroll, this.folderScrollPosition + 1);
    }
  }

  // Check if folder is visible in current scroll position
  isFolderVisible(folderId: number): boolean {
    const folderIndex = this.folders.findIndex(f => f.id === folderId);
    return folderIndex >= this.folderScrollPosition &&
           folderIndex < this.folderScrollPosition + this.visibleFolderCount;
  }

  // Select folder and ensure it's visible
  selectFolder(folderId: number): void {
    this.currentFolder = folderId;

    // Reset navigation history and breadcrumbs when clicking any main folder (tabs at top)
    // This ensures back button disappears when returning to main folders
    this.navigationHistory = [];
    this.breadcrumbs = [];

    // Set currentFolderId for upload/create operations
    if (folderId === 0) {
      this.currentFolderId = null;
    } else {
      this.currentFolderId = folderId;
    }

    // Auto-scroll to make selected folder visible if it's not
    const folderIndex = this.folders.findIndex(f => f.id === folderId);
    if (folderIndex !== -1 && !this.isFolderVisible(folderId)) {
      // Scroll to show the selected folder
      if (folderIndex < this.folderScrollPosition) {
        this.folderScrollPosition = folderIndex;
      } else if (folderIndex >= this.folderScrollPosition + this.visibleFolderCount) {
        this.folderScrollPosition = Math.max(0, folderIndex - this.visibleFolderCount + 1);
      }
    }

    // If "All Media" is clicked (id: 0), fetch all files from main folder
    if (folderId === 0) {
      if (this.galleryFolderId) {
        console.log('All Media clicked - fetching all files from main folder:', this.galleryFolderId);
        this.isLoading = true;
        this.fetchAllMedia(this.galleryFolderId);
      }
      return;
    }

    // For main folders (from top), fetch their contents
    const folder = this.folders.find(f => f.id === folderId);
    if (folder && folderId > 0) {
      console.log('Main folder clicked:', folder.name, folderId);
      this.isLoading = true;
      this.fetchDocumentsForFolder(folderId, folder.name);
    }
  }
}
